import type { GuideBeforeAction, GuideStep } from './types'

export type GuideDataStatus
  = | 'ready'
    | 'loading'
    | 'empty-creatable'
    | 'empty-prerequisite'

export interface GuideDataRule {
  entity: string
  kind: 'creatable' | 'prerequisite'
  rowSelector?: string
  requiredSelector?: string
  emptyTarget: string
  title: string
  description: string
  actionTarget?: string
  prerequisiteTask?: string
  emptyBefore?: GuideBeforeAction
  emptyTargetCandidates?: string[]
}

export interface GuideDataAvailability {
  status: GuideDataStatus
  rule?: GuideDataRule
  title?: string
  description?: string
  targetCandidates: string[]
  entity?: string
}

export type GuideDataEdition = 'sample-library' | 'supplier-network' | 'enterprise'

interface GuideDataSnapshot {
  loading?: boolean
  counts?: Record<string, number>
}

type DataProvider = GuideDataSnapshot
  | (() => GuideDataSnapshot | Promise<GuideDataSnapshot>)
  | { getSnapshot: () => GuideDataSnapshot | Promise<GuideDataSnapshot> }

const EMPTY_TEXT_PATTERN = /暂无|无数据|当前还没有|当前没有|没有可展示|没有找到|未产生|未生成|等待.*数据|no data|empty/i
const observedCounts = new Map<string, number>()

function rule(
  entity: string,
  kind: GuideDataRule['kind'],
  rowSelector: string | undefined,
  emptyTarget: string,
  title: string,
  description: string,
  extras: Partial<GuideDataRule> = {},
): GuideDataRule {
  return { entity, kind, rowSelector, emptyTarget, title, description, ...extras }
}

export const enterpriseDataRules: Record<string, GuideDataRule> = {
  'customer-table': rule('customers', 'creatable', '.customer-page tbody tr[data-id]', '.customer-empty-state, [data-guide-id=\'customer-table\']', '当前还没有客户', '这是新账号的正常状态。客户用于保存需求方、结算、回调和项目归属信息；创建项目前，通常需要先建立客户档案。', { actionTarget: '[data-guide-id=\'customer-new\']' }),
  'customer-analysis': rule('customers', 'prerequisite', '.customer-page tbody tr[data-id]', '.customer-empty-state, [data-guide-id=\'customer-table\']', '客户分析将在有客户后显示', '客户分析需要客户及其项目数据。当前没有数据不代表异常，请先建立客户档案；产生项目数据后再回来查看分析指标。', { prerequisiteTask: 'customer-management' }),
  'supplier-table': rule('suppliers', 'creatable', '.supplier-page tbody tr[data-supplier]', '.supplier-empty-state, [data-guide-id=\'supplier-table\']', '当前还没有供应商', '这是新账号的正常状态。供应商档案用于维护合作范围、价格、结算和回调信息；可以先认识新增入口，教程不会替你保存。', { actionTarget: '[data-guide-id=\'supplier-new\']' }),
  'supplier-members': rule('supplierMembers', 'prerequisite', '.supplier-member-page tbody tr[data-supplier-member], .supplier-member-page tbody tr[data-member]', '.supplier-member-empty-state, [data-guide-id=\'supplier-member-table\']', '当前还没有供应商子会员', '子会员和 UID 映射会在供应商开始带来真实参与数据后出现。当前为空是正常状态，可以先完成供应商建档。', { prerequisiteTask: 'supplier-setup' }),
  'supplier-levels': rule('supplierLevels', 'creatable', '.supplier-level-page tbody tr[data-level], .supplier-level-page tbody tr[data-id]', '.supplier-level-empty-state, [data-guide-id=\'supplier-level-table\']', '当前还没有供应商等级', '供应商等级用于统一管理 B2B、B2C 价格比例。可以先创建等级，再把供应商分配到合适等级。'),
  'cooperation-supplier-detail': rule('cooperationSupplierResources', 'prerequisite', '[data-coop-detail]', '.coop-market-card', '当前没有可打开的云供应商详情', '当前没有可展示的云供应商资源，这不是页面故障。资源出现后，可在详情中核对企业资料、服务区域、交付指标和合作评价。', { prerequisiteTask: 'cooperation-resources' }),
  'cooperation-customer-detail': rule('cooperationCustomerResources', 'prerequisite', '[data-coop-detail]', '.coop-market-card', '当前没有可打开的云客户详情', '当前没有可展示的云客户资源，这不是页面故障。资源出现后，可在详情中核对需求、结算表现、评价和公开联系方式。', { prerequisiteTask: 'cooperation-resources' }),
  'cooperation-applications': rule('cooperationApplications', 'prerequisite', '[data-my-panel=\'sent\'] .coop-request-card, [data-my-panel=\'received\'] .coop-request-card', '[data-my-panel=\'sent\']', '当前还没有合作申请', '在云资源中发出申请，或其他企业向你发出申请后，申请记录才会显示在这里。当前为空是新账号的正常状态。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="sent"]', waitFor: '[data-my-panel="sent"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="sent"] .ui-empty-state-v2', '[data-my-panel="sent"]'],
  }),
  'cooperation-partners': rule('cooperationPartners', 'prerequisite', '[data-my-panel=\'partners\'] .coop-partner-card', '[data-my-panel=\'partners\']', '当前还没有合作伙伴', '只有合作申请通过后，对方才会进入合作伙伴列表。申请通过前，请先核对合作目的、负责人和合作条件。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="partners"]', waitFor: '[data-my-panel="partners"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="partners"] .ui-empty-state-v2', '[data-my-panel="partners"]'],
  }),
  'cooperation-points': rule('cooperationPointLogs', 'prerequisite', '[data-my-panel=\'points\'] tbody tr:not([class*=\'empty\'])', '[data-my-panel=\'points\']', '当前还没有积分明细', '资料解锁、合作触达等资源动作可能产生积分变化。当前没有积分记录不代表账户异常。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="points"]', waitFor: '[data-my-panel="points"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="points"] .ui-empty-state-v2', '[data-my-panel="points"]'],
  }),
  'cooperation-bills': rule('cooperationBills', 'prerequisite', '[data-my-panel=\'bills\'] tbody tr:not([class*=\'empty\'])', '[data-my-panel=\'bills\']', '当前还没有合作账单', '合作账单会在符合计费条件的合作动作发生后产生。当前为空时不需要付款或对账，也不代表系统异常。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="bills"]', waitFor: '[data-my-panel="bills"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="bills"] .ui-empty-state-v2', '[data-my-panel="bills"]'],
  }),
  'project-table': rule('projects', 'creatable', '[data-project-table-body] tr[data-project-id], .project-page tbody tr[data-project-id]', '.project-empty-state, [data-guide-id=\'project-table\']', '当前还没有项目', '这是新账号的正常状态。项目需要客户归属、国家与币种、价格、URL、配额和风险规则；建议先准备客户资料再创建项目草稿。', { actionTarget: '[data-guide-id=\'project-new\']', prerequisiteTask: 'customer-management' }),
  'record-table': rule('surveyRecords', 'prerequisite', '[data-record-table-body] tr[data-record-id]', '.record-table-wrap', '当前还没有调查记录', '调查记录会在项目开始接收访问后产生。当前为空不代表异常；可以先认识查询入口，第一条记录产生后再学习状态和质量排查。', { prerequisiteTask: 'project-create' }),
  'record-export': rule('surveyRecords', 'prerequisite', '[data-record-table-body] tr[data-record-id]', '[data-record-export]', '有调查记录后才能导出', '当前没有可导出的记录。项目产生访问数据后，请先筛选范围、确认权限，再执行导出。', { prerequisiteTask: 'project-create' }),
  'finance-log-table': rule('financeLogs', 'prerequisite', '[data-finance-log-table-body] tr[data-finance-log-id]', '[data-guide-id=\'finance-supplier-log-table\']', '当前还没有供应商资金日志', '财务日志会在项目结算、审核或钱包发生变化后产生。当前为空是正常状态，请先完成项目交付流程。', { prerequisiteTask: 'project-create' }),
  'finance-log-safety': rule('financeLogs', 'prerequisite', '[data-finance-log-table-body] tr[data-finance-log-id]', '[data-guide-id=\'finance-supplier-log-table\']', '有资金变化后再核对日志', '当前没有可追溯的资金变化。产生第一条财务日志后，再核对变动前、变动值、变动后和业务原因。', { prerequisiteTask: 'project-create' }),
  'finance-bill-table': rule('supplierBills', 'prerequisite', '[data-finance-bill-table-body] tr[data-bill-id]', '[data-guide-id=\'finance-supplier-bill-table\']', '当前还没有供应商账单', '供应商账单会在项目进入结算流程后产生。当前没有账单时，不需要执行支付、拒绝或批量处理。', { prerequisiteTask: 'project-create' }),
  'finance-bill-new': rule('suppliers', 'prerequisite', undefined, '[data-guide-id=\'finance-supplier-bill-new\']', '建立供应商后才能发起结算', '新增结算需要真实供应商和可结算业务。请先完成供应商建档与项目交付，再由有权限人员发起结算。', { prerequisiteTask: 'supplier-setup' }),
  'finance-bill-risk': rule('supplierBills', 'prerequisite', '[data-finance-bill-table-body] tr[data-bill-id]', '[data-guide-id=\'finance-supplier-bill-table\'], [data-guide-id=\'finance-supplier-bill-risk-actions\']', '当前没有需要处理的供应商账单', '支付、拒绝和批量操作只会在账单产生后使用。当前为空时请不要为了完成教程创建虚假结算。', { prerequisiteTask: 'project-create' }),
  'finance-invoice-table': rule('customerInvoices', 'prerequisite', '[data-finance-invoice-table-body] tr[data-invoice-id]', '[data-guide-id=\'finance-customer-invoice-table\']', '当前还没有客户发票', '客户发票会在真实应收业务形成后产生。当前为空不代表异常，请先完成客户项目和结算流程。', { prerequisiteTask: 'project-create' }),
  'finance-invoice-risk': rule('customerInvoices', 'prerequisite', '[data-finance-invoice-table-body] tr[data-invoice-id]', '[data-guide-id=\'finance-customer-invoice-table\'], [data-guide-id=\'finance-customer-invoice-risk-actions\']', '当前没有需要处理的客户发票', '开票、作废和下载明细只在真实发票产生后使用。当前教程只说明业务关系，不会创建财务数据。', { prerequisiteTask: 'project-create' }),
  'station-analysis': rule('stationActivity', 'prerequisite', undefined, '[data-guide-id=\'station-analysis-charts\']', '调查站产生业务后才会形成分析图表', '当前没有可用于统计的会员参与或项目数据。先完成会员与项目运营，图表会在数据产生后自动显示。', { prerequisiteTask: 'station-members' }),
  'station-member-table': rule('stationMembers', 'creatable', '[data-station-members-table-body] tr[data-member-id], [data-guide-id=\'station-members-table\'] tbody tr[data-member-id]', '[data-guide-id=\'station-members-table\']', '当前还没有调查站会员', '建议先准备会员组和会员等级，再创建会员。教程可以打开新增表单，但不会替你保存会员资料。', { actionTarget: '[data-guide-id=\'station-members-new\']' }),
  'station-group-table': rule('stationMemberGroups', 'creatable', '[data-guide-id=\'station-member-groups-table\'] tbody tr[data-group-id]', '[data-guide-id=\'station-member-groups-table\']', '当前还没有会员组', '会员组用于组织不同来源或用途的会员。先创建会员组，可以让后续会员归属和运营范围更清楚。'),
  'station-level-table': rule('stationMemberLevels', 'creatable', '[data-guide-id=\'station-member-levels-table\'] tbody tr[data-level-id]', '[data-guide-id=\'station-member-levels-table\']', '当前还没有会员等级', '会员等级用于维护价格比例和运营规则。先建立等级，再创建或调整会员。'),
  'station-schedule-table': rule('stationSchedules', 'prerequisite', '[data-station-schedule-table-body] tr[data-schedule-id]', '[data-guide-id=\'station-schedule-table\']', '当前还没有调查站项目调度', '调度需要可用项目和会员或会员组。当前为空时，请先准备项目及调度对象。', { prerequisiteTask: 'station-members' }),
  'station-schedule-new': rule('projects', 'prerequisite', undefined, '[data-guide-id=\'station-schedule-new\']', '有可用项目后才能新增调度', '新增调度会把项目流量分配给真实会员或会员组。请先创建并确认项目，再配置调度对象和数量。', { prerequisiteTask: 'project-create' }),
  'org-department-table': rule('departments', 'creatable', '[data-guide-id=\'organization-department-table\'] tbody tr[data-department-id]', '[data-guide-id=\'organization-department-table\']', '当前还没有部门', '建议先建立部门层级，再配置角色和员工。部门决定组织归属与主管管理范围。'),
  'org-role-table': rule('roles', 'creatable', '[data-guide-id=\'organization-role-table\'] tbody tr[data-role-id]', '[data-guide-id=\'organization-role-table\']', '当前还没有岗位角色', '角色代表岗位职责和操作权限。建议按项目运营、财务审核或质量管理等职责创建角色。'),
  'org-role-menu': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] tbody tr[data-role-id]', '[data-role-table-body] .role-empty-card, [data-guide-id=\'organization-role-table\']', '当前还没有可配置菜单的角色', '创建至少一个非系统角色后，才能为岗位配置可访问的菜单。当前为空是新账号的正常状态。', {
    requiredSelector: '[data-role-table-body] [data-role-menu]:not([disabled])',
    emptyBefore: { close: ['[data-guide-id="organization-role-new-dialog"]'], waitFor: '[data-role-table-body] .role-empty-card' },
    emptyTargetCandidates: ['[data-role-table-body] .role-empty-card .ui-empty-state-v2', '[data-role-table-body] .role-empty-card', '[data-guide-id="organization-role-table"]'],
  }),
  'org-role-permissions': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] tbody tr[data-role-id]', '[data-role-table-body] .role-empty-card, [data-guide-id=\'organization-role-table\']', '当前还没有可配置权限的角色', '创建至少一个非系统角色后，才能设置操作权限和数据范围。当前不需要强行创建测试角色。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: { close: ['[data-guide-id="organization-role-menu-drawer"]', '[data-guide-id="organization-role-permission-drawer"]'], waitFor: '[data-role-table-body] .role-empty-card' },
    emptyTargetCandidates: ['[data-role-table-body] .role-empty-card .ui-empty-state-v2', '[data-role-table-body] .role-empty-card', '[data-guide-id="organization-role-table"]'],
  }),
  'org-role-diagnostics': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] tbody tr[data-role-id]', '[data-role-table-body] .role-empty-card, [data-guide-id=\'organization-role-table\']', '当前还没有可用于排查权限的角色', '角色创建后，可依次检查账号状态、部门、角色、菜单、操作权限和数据范围；零角色时可先了解排查顺序。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: { close: ['[data-guide-id="organization-role-menu-drawer"]', '[data-guide-id="organization-role-permission-drawer"]'], waitFor: '[data-role-table-body] .role-empty-card' },
    emptyTargetCandidates: ['[data-role-table-body] .role-empty-card .ui-empty-state-v2', '[data-role-table-body] .role-empty-card', '[data-guide-id="organization-role-table"]'],
  }),
  'org-employee-table': rule('employees', 'creatable', '[data-guide-id=\'organization-employee-table\'] tbody tr[data-employee-id]', '[data-guide-id=\'organization-employee-table\']', '当前还没有员工账号', '创建员工前请先准备部门和角色。部门决定归属，角色决定工作能力，账号用于追溯真实操作人。', { prerequisiteTask: 'organization-permissions' }),
  'org-commission-table': rule('commissions', 'creatable', '[data-guide-id=\'organization-commission-table\'] .el-table__body .el-table__row', '[data-guide-id=\'organization-commission-table\']', '当前还没有提成规则', '提成规则会影响真实收入计算。请先确认适用员工、触发条件、计算对象和比例，再由授权人员创建。'),
  'config-screen-table': rule('screenTemplates', 'creatable', '[data-guide-id=\'config-screen-table\'] tbody tr[data-screen-id]', '[data-guide-id=\'config-screen-table\']', '当前还没有问卷模板', '可以从题型、画布和属性开始创建第一份测试问卷。正式使用前应验证填写体验、逻辑和兼容性。'),
  'config-events': rule('eventRules', 'creatable', '[data-guide-id=\'config-events-table\'] tbody tr[data-event-id]', '[data-guide-id=\'config-events-table\']', '当前还没有事件通知规则', '事件、接收对象和启停状态共同决定通知结果。可以先了解结构，再按真实业务需要创建。'),
  'config-security-table': rule('securityRules', 'creatable', '[data-guide-id=\'config-security-table\'] tbody tr[data-security-id]', '[data-guide-id=\'config-security-table\']', '当前还没有系统安检规则', '安检规则会影响客户和项目的风险判断。上线前请先确认产品、环境、凭据和默认阻止逻辑。'),
}

/**
 * 样本库运营版只对压缩包中声明的 24 个数据依赖步骤进行分流。
 * 选择器使用当前 Vue / Element Plus 页面的稳定 data-guide-id，业务路由仍由后端 routeList 解析。
 */
export const sampleLibraryDataRules: Record<string, GuideDataRule> = {
  'customer-table': rule('customers', 'creatable', '[data-guide-id=\'customer-table\'] .el-table__body .el-table__row', '[data-guide-id=\'customer-table\']', '当前还没有客户档案', '这是新账号第一次使用时的正常状态。客户资料创建后，会显示在当前列表中，并成为项目归属、结算和回调规则的来源。', {
    actionTarget: '[data-guide-id=\'customer-new\']',
  }),
  'station-level-table': rule('stationMemberLevels', 'creatable', '[data-guide-id=\'station-member-levels-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-member-levels-table\']', '当前还没有会员等级', '这是新账号的正常状态。会员等级用于统一价格比例和运营规则，应在创建会员前完成。'),
  'station-group-table': rule('stationMemberGroups', 'creatable', '[data-guide-id=\'station-member-groups-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-member-groups-table\']', '当前还没有会员组', '这是新账号的正常状态。会员组用于管理负责人、接单范围和工作室规则。'),
  'station-member-table': rule('stationMembers', 'creatable', '[data-guide-id=\'station-members-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-members-table\']', '当前还没有会员', '这是新账号的正常状态。会员创建后，登录状态、开放渠道、等级和会员组会显示在这里。', {
    actionTarget: '[data-guide-id=\'station-members-new\']',
  }),
  'project-table': rule('projects', 'creatable', '[data-guide-id=\'project-table\'] .el-table__body .el-table__row', '[data-guide-id=\'project-table\']', '当前还没有项目', '这是新账号的正常状态。项目创建后，草稿、运行、结束和会员分配状态会统一显示在这里。', {
    actionTarget: '[data-guide-id=\'project-new\']',
    prerequisiteTask: 'customer-management',
  }),
  'cooperation-customer-market': rule('cooperationCustomerResources', 'prerequisite', '[data-guide-id=\'cooperation-market-module\'] .coop-market-card', '[data-guide-id=\'cooperation-market-module\']', '当前还没有可筛选的云客户资源', '客户合作机会需要对方发布或同步真实需求后才会出现。当前为空是新账号的正常状态。', {
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
  }),
  'cooperation-customer-detail': rule('cooperationCustomerResources', 'prerequisite', '[data-guide-id=\'cooperation-market-module\'] .coop-market-card', '[data-guide-id=\'cooperation-market-module\']', '当前没有可查看的客户资源详情', '详情抽屉必须由真实客户资源打开。没有资源时，教程会停留在资源市场说明产生条件，不会错误点击页面。', {
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
  }),
  'cooperation-applications': rule('cooperationApplications', 'prerequisite', '[data-my-panel=\'sent\'] .coop-request-card, [data-my-panel=\'received\'] .coop-request-card', '[data-my-panel=\'sent\']', '当前还没有合作申请', '合作申请只会在你向真实资源发起申请，或收到其他企业申请后产生。当前为空是正常状态。', {
    emptyBefore: { tab: '[data-my-tab="sent"]', waitFor: '[data-my-panel="sent"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="sent"] .ui-empty-state-v2', '[data-my-panel="sent"]'],
  }),
  'cooperation-partners': rule('cooperationPartners', 'prerequisite', '[data-my-panel=\'partners\'] .coop-partner-card', '[data-my-panel=\'partners\']', '当前还没有已建立的合作关系', '只有合作申请通过并完成双方确认后，合作对象才会显示在这里。当前为空不是系统异常。', {
    emptyBefore: { tab: '[data-my-tab="partners"]', waitFor: '[data-my-panel="partners"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="partners"] .ui-empty-state-v2', '[data-my-panel="partners"]'],
  }),
  'cooperation-points': rule('cooperationPointLogs', 'prerequisite', '[data-my-panel=\'points\'] tbody tr:not([class*=\'empty\'])', '[data-my-panel=\'points\']', '当前还没有点数记录', '购买点数、发起合作或解锁联系方式后才会形成点数流水。新账号没有记录属于正常状态。', {
    emptyBefore: { tab: '[data-my-tab="points"]', waitFor: '[data-my-panel="points"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="points"] .ui-empty-state-v2', '[data-my-panel="points"]'],
  }),
  'cooperation-bills': rule('cooperationBills', 'prerequisite', '[data-my-panel=\'bills\'] tbody tr:not([class*=\'empty\'])', '[data-my-panel=\'bills\']', '当前还没有合作账单', '合作关系进入真实结算周期后才会生成账单。当前没有账单是新账号的正常状态。', {
    emptyBefore: { tab: '[data-my-tab="bills"]', waitFor: '[data-my-panel="bills"]:not([hidden])' },
    emptyTargetCandidates: ['[data-my-panel="bills"] .ui-empty-state-v2', '[data-my-panel="bills"]'],
  }),
  'station-analysis-kpis': rule('stationActivity', 'prerequisite', '[data-guide-id=\'station-analysis-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-analysis-kpis\']', '当前还没有会员运营指标', '参与数、完成数和结算指标来自会员真实参与项目后的数据。新账号没有指标是正常状态。', {
    prerequisiteTask: 'station-members',
  }),
  'station-analysis-charts': rule('stationActivity', 'prerequisite', '[data-guide-id=\'station-analysis-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-analysis-charts\']', '当前还没有可生成趋势图的数据', '趋势与结构图需要会员参与记录形成时间序列。当前没有图表数据不是系统异常。', {
    prerequisiteTask: 'station-members',
  }),
  'station-analysis-table': rule('stationActivity', 'prerequisite', '[data-guide-id=\'station-analysis-table\'] .el-table__body .el-table__row', '[data-guide-id=\'station-analysis-table\']', '当前还没有会员排行数据', '会员指标排行需要真实参与、完成和结算记录。新账号没有排行属于正常状态。', {
    prerequisiteTask: 'station-members',
  }),
  'finance-log-table': rule('memberFinanceLogs', 'prerequisite', '[data-guide-id=\'finance-member-log-table\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-member-log-table\']', '当前还没有会员财务流水', '会员财务日志来自真实参与、奖励、扣款或结算变动。新账号没有流水是正常状态。', {
    prerequisiteTask: 'project-create',
  }),
  'finance-employee-log': rule('employeeFinanceLogs', 'prerequisite', '[data-guide-id=\'finance-employee-log-page\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-employee-log-page\'] .finance-log-table-wrap', '当前还没有员工财务流水', '员工财务日志只记录真实的员工收入、调整或结算变动。没有业务发生时不会生成记录。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap .el-table__empty-block',
      '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap',
    ],
  }),
  'finance-bill-table': rule('memberBills', 'prerequisite', '[data-guide-id=\'finance-member-bill-table\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-member-bill-table\']', '当前还没有会员账单', '会员账单需要由已确认的真实会员流水汇总生成。当前为空是新账号的正常状态。', {
    prerequisiteTask: 'project-create',
  }),
  'finance-bill-risk': rule('memberBills', 'prerequisite', '[data-guide-id=\'finance-member-bill-table\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-member-bill-table\']', '当前没有可审核或支付的会员账单', '创建、审核和支付必须基于真实会员账单。当前为空时，不展开工具栏，也不会执行任何操作。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-member-bill-table"] .el-table__empty-block',
      '[data-guide-id="finance-member-bill-table"]',
    ],
  }),
  'finance-employee-bill': rule('employeeBills', 'prerequisite', '[data-guide-id=\'finance-employee-bill-page\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-employee-bill-page\'] .pm-settlement-table-wrap', '当前还没有员工账单', '员工账单需要由已确认的员工流水汇总生成。没有真实流水时不会出现账单。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap .el-table__empty-block',
      '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap',
    ],
  }),
  'finance-invoice-table': rule('customerInvoices', 'prerequisite', '[data-guide-id=\'finance-customer-invoice-table\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-customer-invoice-table\']', '当前还没有客户发票', '客户发票建立在真实结算确认之上。新账号尚未发生结算时，发票列表为空是正常状态。', {
    prerequisiteTask: 'project-create',
  }),
  'finance-invoice-risk': rule('customerInvoices', 'prerequisite', '[data-guide-id=\'finance-customer-invoice-table\'] .el-table__body .el-table__row', '[data-guide-id=\'finance-customer-invoice-table\']', '当前没有可开具或作废的发票', '开票、作废和删除必须基于真实发票记录。当前为空时，教程不会执行任何业务操作。', {
    prerequisiteTask: 'project-create',
  }),
  'org-role-menu': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] .el-table__body .el-table__row', '[data-guide-id=\'organization-role-table\']', '当前还没有可配置的角色', '菜单权限必须配置到一条真实角色记录上。当前没有角色时，教程不会点击不存在的表格行。', {
    requiredSelector: '[data-role-table-body] [data-role-menu]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: ['[data-guide-id="organization-role-table"] .el-table__empty-block', '[data-guide-id="organization-role-table"]'],
  }),
  'org-role-permissions': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] .el-table__body .el-table__row', '[data-guide-id=\'organization-role-table\']', '当前还没有可授权的角色', '操作权限和数据范围需要绑定到真实角色。当前没有角色时，页面为空属于正常状态。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: ['[data-guide-id="organization-role-table"] .el-table__empty-block', '[data-guide-id="organization-role-table"]'],
  }),
  'org-role-diagnostics': rule('roles', 'prerequisite', '[data-guide-id=\'organization-role-table\'] .el-table__body .el-table__row', '[data-guide-id=\'organization-role-table\']', '当前没有可用于权限诊断的角色', '权限诊断需要同时检查真实角色的菜单、操作和数据范围。当前为空不会中断后续员工与佣金教程。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: ['[data-guide-id="organization-role-table"] .el-table__empty-block', '[data-guide-id="organization-role-table"]'],
  }),
}

/**
 * 供应商网络运营版的数据依赖规则。
 *
 * 只覆盖该版本实际存在且依赖真实业务记录的步骤；企业版和样本库运营版
 * 仍分别使用原有 enterpriseDataRules / sampleLibraryDataRules，不复用本规则。
 */
export const supplierNetworkDataRules: Record<string, GuideDataRule> = {
  'customer-table': rule('customers', 'creatable', '[data-guide-id="customer-table"] .el-table__body .el-table__row', '[data-guide-id="customer-table"]', '当前还没有客户档案', '这是新账号第一次使用时的正常状态。客户资料创建后，会显示在当前列表中，并成为项目归属、结算和回调规则的来源。', {
    actionTarget: '[data-guide-id="customer-new"]',
    emptyTargetCandidates: [
      '[data-guide-id="customer-table"] .business-empty-state',
      '[data-guide-id="customer-table"] .el-table__empty-block',
      '[data-guide-id="customer-table"]',
    ],
  }),
  'supplier-levels': rule('supplierLevels', 'creatable', '[data-guide-id="supplier-level-table"] .el-table__body .el-table__row', '[data-guide-id="supplier-level-table"]', '当前还没有供应商等级', '这是新账号的正常状态。供应商等级用于统一价格比例和结算规则，应先于供应商档案创建。', {
    emptyTargetCandidates: [
      '[data-guide-id="supplier-level-table"] .business-empty-state',
      '[data-guide-id="supplier-level-table"] .el-table__empty-block',
      '[data-guide-id="supplier-level-table"]',
    ],
  }),
  'project-table': rule('projects', 'creatable', '[data-guide-id="project-table"] .el-table__body .el-table__row', '[data-guide-id="project-table"]', '当前还没有项目', '这是新账号的正常状态。项目创建后，草稿、运行、结束和供应商分配状态会统一显示在这里。', {
    actionTarget: '[data-guide-id="project-new"]',
    prerequisiteTask: 'customer-management',
    emptyTargetCandidates: [
      '[data-guide-id="project-table"] .business-empty-state',
      '[data-guide-id="project-table"] .el-table__empty-block',
      '[data-guide-id="project-table"]',
    ],
  }),
  'cooperation-supplier-market': rule('cooperationSupplierResources', 'prerequisite', '[data-guide-id="cooperation-market-module"] .coop-market-card [data-coop-detail]', '[data-guide-id="cooperation-market-module"]', '当前还没有可筛选的云供应商资源', '资源市场只有在供应商完成对外资料并开放真实资源后才会显示候选项。当前为空不代表系统异常。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]', '[data-coop-detail-layer]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="cooperation-market-module"] > .business-empty-state',
      '[data-guide-id="cooperation-market-module"] .business-empty-state',
      '[data-guide-id="cooperation-market-module"]',
    ],
  }),
  'cooperation-supplier-detail': rule('cooperationSupplierResources', 'prerequisite', '[data-guide-id="cooperation-market-module"] .coop-market-card [data-coop-detail]', '[data-guide-id="cooperation-market-module"]', '当前没有可查看的供应商资源详情', '详情抽屉必须由一条真实资源打开。当前没有资源时，教程不会点击不存在的记录，也不会伪造合作数据。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]', '[data-coop-detail-layer]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="cooperation-market-module"] > .business-empty-state',
      '[data-guide-id="cooperation-market-module"] .business-empty-state',
      '[data-guide-id="cooperation-market-module"]',
    ],
  }),
  'cooperation-customer-market': rule('cooperationCustomerResources', 'prerequisite', '[data-guide-id="cooperation-market-module"] .coop-market-card [data-coop-detail]', '[data-guide-id="cooperation-market-module"]', '当前还没有可筛选的云客户资源', '客户合作机会需要对方发布或同步真实需求后才会出现。当前为空是新账号的正常状态。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]', '[data-coop-detail-layer]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="cooperation-market-module"] > .business-empty-state',
      '[data-guide-id="cooperation-market-module"] .business-empty-state',
      '[data-guide-id="cooperation-market-module"]',
    ],
  }),
  'cooperation-customer-detail': rule('cooperationCustomerResources', 'prerequisite', '[data-guide-id="cooperation-market-module"] .coop-market-card [data-coop-detail]', '[data-guide-id="cooperation-market-module"]', '当前没有可查看的客户资源详情', '详情抽屉必须由真实客户资源打开。没有资源时，教程会停留在资源市场说明产生条件，不会错误点击页面。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: {
      close: ['[data-guide-id="cooperation-detail-module"]', '[data-coop-detail-layer]'],
      waitFor: '[data-guide-id="cooperation-market-module"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="cooperation-market-module"] > .business-empty-state',
      '[data-guide-id="cooperation-market-module"] .business-empty-state',
      '[data-guide-id="cooperation-market-module"]',
    ],
  }),
  'cooperation-applications': rule('cooperationApplications', 'prerequisite', '[data-my-panel="sent"] .cr-request-card', '[data-my-panel="sent"]', '当前还没有合作申请', '合作申请只会在你向真实资源发起申请，或收到其他企业申请后产生。当前为空是正常状态。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="sent"]', waitFor: '[data-my-panel="sent"]' },
    emptyTargetCandidates: [
      '[data-my-panel="sent"] .business-empty-state',
      '[data-my-panel="sent"]',
    ],
  }),
  'cooperation-partners': rule('cooperationPartners', 'prerequisite', '[data-my-panel="partners"] .cr-request-card', '[data-my-panel="partners"]', '当前还没有已建立的合作关系', '只有合作申请通过并完成双方确认后，合作对象才会显示在这里。当前为空不是系统异常。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="partners"]', waitFor: '[data-my-panel="partners"]' },
    emptyTargetCandidates: [
      '[data-my-panel="partners"] .business-empty-state',
      '[data-my-panel="partners"]',
    ],
  }),
  'cooperation-points': rule('cooperationPointLogs', 'prerequisite', '[data-my-panel="points"] .el-table__body .el-table__row', '[data-my-panel="points"]', '当前还没有点数记录', '购买点数、发起合作或解锁联系方式后才会形成点数流水。新账号没有记录属于正常状态。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="points"]', waitFor: '[data-my-panel="points"]' },
    emptyTargetCandidates: [
      '[data-my-panel="points"] .business-empty-state',
      '[data-my-panel="points"] .el-table__empty-block',
      '[data-my-panel="points"]',
    ],
  }),
  'cooperation-bills': rule('cooperationBills', 'prerequisite', '[data-my-panel="bills"] .el-table__body .el-table__row', '[data-my-panel="bills"]', '当前还没有合作账单', '合作关系进入真实结算周期后才会生成账单。当前没有账单是新账号的正常状态。', {
    prerequisiteTask: 'cooperation-resources',
    emptyBefore: { tab: '[data-my-tab="bills"]', waitFor: '[data-my-panel="bills"]' },
    emptyTargetCandidates: [
      '[data-my-panel="bills"] .business-empty-state',
      '[data-my-panel="bills"] .el-table__empty-block',
      '[data-my-panel="bills"]',
    ],
  }),
  'finance-log-table': rule('supplierFinanceLogs', 'prerequisite', '[data-guide-id="finance-supplier-log-table"] .el-table__body .el-table__row', '[data-guide-id="finance-supplier-log-table"]', '当前还没有供应商财务流水', '供应商财务日志来自真实充值、扣款、项目交付或结算变动。新账号没有流水是正常状态。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-supplier-log-table"] .business-empty-state',
      '[data-guide-id="finance-supplier-log-table"] .el-table__empty-block',
      '[data-guide-id="finance-supplier-log-table"]',
    ],
  }),
  'finance-employee-log': rule('employeeFinanceLogs', 'prerequisite', '[data-guide-id="finance-employee-log-page"] .el-table__body .el-table__row', '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap', '当前还没有员工财务流水', '员工财务日志只记录真实的员工收入、调整或结算变动。没有业务发生时不会生成记录。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap .business-empty-state',
      '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap .el-table__empty-block',
      '[data-guide-id="finance-employee-log-page"] .finance-log-table-wrap',
    ],
  }),
  'finance-bill-table': rule('supplierBills', 'prerequisite', '[data-guide-id="finance-supplier-bill-table"] .el-table__body .el-table__row', '[data-guide-id="finance-supplier-bill-table"]', '当前还没有供应商账单', '供应商账单需要由已确认的真实项目流水汇总生成。当前为空是新账号的正常状态。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-supplier-bill-table"] .business-empty-state',
      '[data-guide-id="finance-supplier-bill-table"] .el-table__empty-block',
      '[data-guide-id="finance-supplier-bill-table"]',
    ],
  }),
  'finance-bill-risk': rule('supplierBills', 'prerequisite', '[data-guide-id="finance-supplier-bill-table"] .el-table__body .el-table__row', '[data-guide-id="finance-supplier-bill-table"]', '当前没有可审核或支付的供应商账单', '创建、审核和支付必须基于真实账单。当前为空时，教程不会尝试操作不存在的账单。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-supplier-bill-table"] .business-empty-state',
      '[data-guide-id="finance-supplier-bill-table"] .el-table__empty-block',
      '[data-guide-id="finance-supplier-bill-table"]',
    ],
  }),
  'finance-employee-bill': rule('employeeBills', 'prerequisite', '[data-guide-id="finance-employee-bill-page"] .el-table__body .el-table__row', '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap', '当前还没有员工账单', '员工账单需要由已确认的员工流水汇总生成。没有真实流水时不会出现账单。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap .business-empty-state',
      '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap .el-table__empty-block',
      '[data-guide-id="finance-employee-bill-page"] .pm-settlement-table-wrap',
    ],
  }),
  'finance-invoice-table': rule('customerInvoices', 'prerequisite', '[data-guide-id="finance-customer-invoice-table"] .el-table__body .el-table__row', '[data-guide-id="finance-customer-invoice-table"]', '当前还没有客户发票', '客户发票建立在真实结算确认之上。新账号尚未发生结算时，发票列表为空是正常状态。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-customer-invoice-table"] .business-empty-state',
      '[data-guide-id="finance-customer-invoice-table"] .el-table__empty-block',
      '[data-guide-id="finance-customer-invoice-table"]',
    ],
  }),
  'finance-invoice-risk': rule('customerInvoices', 'prerequisite', '[data-guide-id="finance-customer-invoice-table"] .el-table__body .el-table__row', '[data-guide-id="finance-customer-invoice-table"]', '当前没有可开具或作废的发票', '开票、作废和删除必须基于真实发票记录。当前为空时，教程不会执行任何业务操作。', {
    prerequisiteTask: 'project-create',
    emptyTargetCandidates: [
      '[data-guide-id="finance-customer-invoice-table"] .business-empty-state',
      '[data-guide-id="finance-customer-invoice-table"] .el-table__empty-block',
      '[data-guide-id="finance-customer-invoice-table"]',
    ],
  }),
  'org-role-menu': rule('roles', 'prerequisite', '[data-guide-id="organization-role-table"] .el-table__body .el-table__row', '[data-guide-id="organization-role-table"]', '当前还没有可配置的角色', '菜单权限必须配置到一条真实角色记录上。当前没有角色时，教程不会点击不存在的表格行。', {
    requiredSelector: '[data-role-table-body] [data-role-menu]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="organization-role-table"] .business-empty-state',
      '[data-guide-id="organization-role-table"] .el-table__empty-block',
      '[data-guide-id="organization-role-table"]',
    ],
  }),
  'org-role-permissions': rule('roles', 'prerequisite', '[data-guide-id="organization-role-table"] .el-table__body .el-table__row', '[data-guide-id="organization-role-table"]', '当前还没有可授权的角色', '操作权限和数据范围需要绑定到真实角色。当前没有角色时，页面为空属于正常状态。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="organization-role-table"] .business-empty-state',
      '[data-guide-id="organization-role-table"] .el-table__empty-block',
      '[data-guide-id="organization-role-table"]',
    ],
  }),
  'org-role-diagnostics': rule('roles', 'prerequisite', '[data-guide-id="organization-role-table"] .el-table__body .el-table__row', '[data-guide-id="organization-role-table"]', '当前没有可用于权限诊断的角色', '权限诊断需要同时检查真实角色的菜单、操作和数据范围。当前为空不会中断后续员工与佣金教程。', {
    requiredSelector: '[data-role-table-body] [data-role-permission]:not([disabled])',
    emptyBefore: {
      close: ['[data-guide-id="organization-role-new-dialog"]', '[data-guide-id="organization-role-menu-panel"]', '[data-guide-id="organization-role-permission-panel"]'],
      waitFor: '[data-guide-id="organization-role-table"]',
    },
    emptyTargetCandidates: [
      '[data-guide-id="organization-role-table"] .business-empty-state',
      '[data-guide-id="organization-role-table"] .el-table__empty-block',
      '[data-guide-id="organization-role-table"]',
    ],
  }),
}

export function getGuideDataRules(
  editionId: GuideDataEdition,
): Record<string, GuideDataRule> {
  if (editionId === 'sample-library') {
    return sampleLibraryDataRules
  }
  if (editionId === 'enterprise') {
    return enterpriseDataRules
  }
  if (editionId === 'supplier-network') {
    return supplierNetworkDataRules
  }
  return {}
}

/**
 * 公共规则查询入口。GuideTour 与数据状态评估统一通过该方法读取规则，
 * 避免各组件自行判断版本；三个版本仍返回各自独立的规则对象。
 */
export function getGuideDataRule(
  editionId: GuideDataEdition,
  stepId: string,
): GuideDataRule | undefined {
  return getGuideDataRules(editionId)[stepId]
}

function selectorList(value: string) {
  return value.split(',').map(item => item.trim()).filter(Boolean)
}

function safeQueryAll(selector: string, scope: ParentNode = document) {
  try {
    return Array.from(scope.querySelectorAll<HTMLElement>(selector))
  }
  catch {
    return []
  }
}

function isRendered(element: HTMLElement) {
  if (!element.isConnected) {
    return false
  }
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) {
    return false
  }
  const style = window.getComputedStyle(element)
  return style.display !== 'none'
    && style.visibility !== 'hidden'
    && style.visibility !== 'collapse'
    && element.getClientRects().length > 0
}

function isRealDataNode(element: HTMLElement) {
  if (!isRendered(element)) {
    return false
  }
  if (element.matches([
    '[data-runtime-empty-row]',
    '.enterprise-empty-row',
    '.ui-empty-state-row',
    '[class*="empty-state"]',
    '[class*="-empty"]',
  ].join(', '))) {
    return false
  }
  return !EMPTY_TEXT_PATTERN.test(String(element.textContent || '').trim())
}

function pageText(scope: ParentNode) {
  return scope instanceof HTMLElement ? scope.textContent || '' : ''
}

function pageState(scope: ParentNode) {
  const loading = safeQueryAll([
    '.el-loading-mask',
    '[aria-busy="true"]',
    '[data-loading="true"]',
    '.business-empty-state.is-loading',
  ].join(', '), scope).some(isRendered)
  return { loading }
}

function rowsInsideTarget(ruleValue: GuideDataRule) {
  const containers = selectorList(ruleValue.emptyTarget)
    .flatMap(selector => safeQueryAll(selector))
    .filter(isRendered)
  for (const container of containers) {
    const rows = safeQueryAll('.el-table__body .el-table__row, tbody tr:not([class*="empty"]), [data-row-id]', container)
      .filter(isRendered)
    if (rows.length) {
      return rows.length
    }
    const explicitEmptyState = safeQueryAll([
      '.el-table__empty-block',
      '.el-empty',
      '.ui-empty-state-v2',
      '[data-runtime-empty-row]',
      '.enterprise-empty-row',
    ].join(', '), container).some(isRendered)
    if (explicitEmptyState || EMPTY_TEXT_PATTERN.test(container.textContent || '')) {
      return 0
    }
  }
  return null
}

function domCount(ruleValue: GuideDataRule, scope: ParentNode) {
  if (ruleValue.requiredSelector) {
    return safeQueryAll(ruleValue.requiredSelector).some(isRendered) ? 1 : 0
  }

  // 真实行/卡片必须优先于空态文本判断。部分页面的容器会保留辅助说明，
  // 即使数据已经渲染，说明中仍可能出现“暂无”等字样。
  if (ruleValue.rowSelector) {
    const declaredRows = safeQueryAll(ruleValue.rowSelector).filter(isRealDataNode)
    if (declaredRows.length) {
      return declaredRows.length
    }
  }

  const targetCount = rowsInsideTarget(ruleValue)
  if (targetCount !== null) {
    return targetCount
  }

  if (ruleValue.rowSelector) {
    if (EMPTY_TEXT_PATTERN.test(pageText(scope))) {
      return 0
    }
  }
  return null
}

async function providerSnapshot(dataRules: Record<string, GuideDataRule>): Promise<GuideDataSnapshot> {
  const source = window as Window & {
    SurveyEnterpriseOnboardingDataProvider?: DataProvider
    SurveyOnboardingDataProvider?: DataProvider
    SurveyEnterpriseOnboardingDataSnapshot?: GuideDataSnapshot
  }
  const providers = [source.SurveyEnterpriseOnboardingDataProvider, source.SurveyOnboardingDataProvider]
  const merged: GuideDataSnapshot = { counts: {} }

  const mergeSnapshot = (value: GuideDataSnapshot | undefined) => {
    if (!value) {
      return
    }
    merged.loading ||= value.loading === true
    Object.entries(value.counts || {}).forEach(([key, count]) => {
      const numeric = Number(count)
      if (Number.isFinite(numeric)) {
        merged.counts![key] = Math.max(0, numeric)
      }
    })
  }

  const dataset = {
    ...(document.documentElement?.dataset || {}),
    ...(document.body?.dataset || {}),
  }
  const datasetCounts: Record<string, number> = {}
  Object.values(dataRules).forEach(({ entity }) => {
    const datasetKey = `onboardingCount${entity.charAt(0).toUpperCase()}${entity.slice(1)}`
    const numeric = Number(dataset[datasetKey as keyof typeof dataset])
    if (Number.isFinite(numeric)) {
      datasetCounts[entity] = Math.max(0, numeric)
    }
  })
  mergeSnapshot({
    loading: dataset.onboardingDataState === 'loading',
    counts: datasetCounts,
  })
  if (source.SurveyEnterpriseOnboardingDataSnapshot) {
    mergeSnapshot(source.SurveyEnterpriseOnboardingDataSnapshot)
  }
  for (const provider of providers) {
    if (!provider) {
      continue
    }
    try {
      const value = typeof provider === 'function'
        ? await provider()
        : 'getSnapshot' in provider ? await provider.getSnapshot() : provider
      mergeSnapshot(value)
    }
    catch {
      continue
    }
  }
  return merged
}

export async function evaluateGuideStepData(
  step: GuideStep,
  scope: ParentNode,
  editionId: GuideDataEdition,
): Promise<GuideDataAvailability> {
  const dataRules = getGuideDataRules(editionId)
  const ruleValue = getGuideDataRule(editionId, step.id)
  await new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()))
  const snapshot = await providerSnapshot(dataRules)
  const state = pageState(scope)

  // Loading is an internal page state, not an onboarding step. GuideTour waits for
  // the current business page to finish loading without rendering an extra step.
  if (snapshot.loading || state.loading) {
    return { status: 'loading', targetCandidates: [] }
  }
  if (!ruleValue) {
    return { status: 'ready', targetCandidates: [] }
  }

  const candidates = [
    ...(ruleValue.emptyTargetCandidates || []),
    ...selectorList(ruleValue.emptyTarget),
    ...step.selectors,
    'main',
    '#app-main',
  ]
  const base = {
    rule: ruleValue,
    title: ruleValue.title,
    description: ruleValue.description,
    targetCandidates: [...new Set(candidates)],
    entity: ruleValue.entity,
  }

  const currentDomCount = domCount(ruleValue, scope)
  if (currentDomCount !== null) {
    observedCounts.set(ruleValue.entity, currentDomCount)
  }
  const suppliedCount = Number(snapshot.counts?.[ruleValue.entity])
  const count = currentDomCount
    ?? (Number.isFinite(suppliedCount) ? Math.max(0, suppliedCount) : undefined)
    ?? observedCounts.get(ruleValue.entity)
  if (count !== 0) {
    return { status: 'ready', targetCandidates: [] }
  }

  return {
    ...base,
    status: ruleValue.kind === 'creatable' ? 'empty-creatable' : 'empty-prerequisite',
  }
}

export function evaluateEnterpriseGuideStep(
  step: GuideStep,
  scope: ParentNode,
) {
  return evaluateGuideStepData(step, scope, 'enterprise')
}

export function isDeferredAvailability(availability: GuideDataAvailability) {
  return availability.status === 'empty-creatable' || availability.status === 'empty-prerequisite'
}

export function resetEnterpriseDataContext() {
  observedCounts.clear()
}
